import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', href: '#hero', path: '/' },
    { name: 'Carte', href: '#menu', path: '/' },
    { name: 'Galerie', href: '/galerie', path: '/galerie' },
    { name: 'Contact', href: '#contact', path: '/' },
  ];

  const handleNavClick = (link) => {
    if (link.path === '/galerie') {
      navigate('/galerie');
    } else if (link.path === '/' && !isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(link.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToSection = (href) => {
    if (href === '#hero' && !isHomePage) {
      navigate('/');
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleContactClick = () => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector('#contact');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      scrollToSection('#contact');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-medium'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('#hero')}
            className="flex items-center space-x-3 transition-smooth hover:opacity-90"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_page-builder-55/artifacts/ptld52ee_295233728_472735281522011_7002067538974660912_n.png"
              alt="P&G Pizza & Gourmandises"
              className="h-12 sm:h-14 w-auto"
            />
            <div className="font-display text-2xl sm:text-3xl text-primary tracking-wide">
              P&G
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 relative group ${
                  isScrolled 
                    ? 'text-foreground hover:text-primary' 
                    : 'text-background/90 hover:text-background'
                }`}
              >
                {link.name}
                <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-300 ${
                  isScrolled ? 'bg-primary' : 'bg-background'
                }`} />
              </button>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              onClick={handleContactClick}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-soft hover:shadow-medium transition-all"
            >
              <Phone className="w-4 h-4 mr-2" />
              Commander
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className={`transition-colors duration-300 ${
                isScrolled ? 'text-foreground' : 'text-background'
              }`}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <nav className="flex flex-col space-y-4 mt-8">
                {navLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className="text-left px-4 py-3 text-lg font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-smooth"
                  >
                    {link.name}
                  </button>
                ))}
                <Button
                  onClick={handleContactClick}
                  className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Commander
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;